import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Clock, Tag } from "@phosphor-icons/react";
import axios from "axios";
import useLenis from "../lib/useLenis";
import { initAnalytics } from "../lib/analytics";
import SEO from "../components/SEO";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ScrollProgress from "../components/ScrollProgress";
import CookieBanner from "../components/CookieBanner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

function resolveCover(url) {
    if (!url) return null;
    if (/^https?:\/\//i.test(url)) return url;
    return `${BACKEND}${url.startsWith("/") ? "" : "/"}${url}`;
}

export default function BlogIndex() {
    useLenis();
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [category, setCategory] = useState(null);

    useEffect(() => {
        initAnalytics();
        setLoading(true);
        axios
            .get(`${API}/blog`)
            .then(({ data }) => setArticles(data))
            .finally(() => setLoading(false));
    }, []);

    const categories = Array.from(
        new Set(articles.map((a) => a.category).filter(Boolean)),
    );
    const filtered = category
        ? articles.filter((a) => a.category === category)
        : articles;

    return (
        <main data-testid="blog-index" className="bg-scd text-cream">
            <SEO
                title="Journal — création web, photo, vidéo à Magog"
                description="Guides, conseils et coulisses signés Studio Cent Douze — création de sites web, photo, vidéo, création de contenu et branding pour PME au Québec et en Estrie."
                path="/blog"
                ogSlug="home"
            />
            <ScrollProgress />
            <Header />

            <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 px-6 md:px-12 lg:px-16">
                <div className="max-w-5xl mx-auto">
                    <span className="label-eyebrow text-yellow">— Journal</span>
                    <h1
                        className="mt-3 font-display font-semibold text-cream leading-[0.95] tracking-tight"
                        style={{
                            fontSize: "clamp(2.5rem, 7vw, 5.5rem)",
                            letterSpacing: "-0.03em",
                        }}
                    >
                        Guides, conseils et coulisses.
                    </h1>
                    <p className="mt-6 max-w-2xl text-cream/75 text-lg">
                        Nos réflexions sur la création web, la photo, la
                        vidéo, la création de contenu et le branding — pour
                        les PME qui veulent grandir intelligemment.
                    </p>

                    {/* Category filter */}
                    {categories.length > 1 && (
                        <div
                            data-testid="blog-categories"
                            className="mt-10 flex flex-wrap gap-2"
                        >
                            <CategoryPill
                                label="Tous"
                                active={!category}
                                onClick={() => setCategory(null)}
                            />
                            {categories.map((c) => (
                                <CategoryPill
                                    key={c}
                                    label={c}
                                    active={category === c}
                                    onClick={() => setCategory(c)}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </section>

            <section className="pb-32 px-6 md:px-12 lg:px-16">
                <div className="max-w-6xl mx-auto">
                    {loading ? (
                        <p className="label-eyebrow text-cream/50 text-center py-16">
                            Chargement…
                        </p>
                    ) : filtered.length === 0 ? (
                        <p className="label-eyebrow text-cream/50 text-center py-16">
                            Aucun article pour l&apos;instant.
                        </p>
                    ) : (
                        <div
                            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
                            data-testid="blog-list"
                        >
                            {filtered.map((a, i) => (
                                <ArticleCard key={a.id} article={a} index={i} />
                            ))}
                        </div>
                    )}
                </div>
            </section>

            <Footer />
            <CookieBanner />
        </main>
    );
}

function CategoryPill({ label, active, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={`blog-cat-${label.toLowerCase()}`}
            className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors border ${
                active
                    ? "bg-yellow text-[#272727] border-yellow"
                    : "border-scd text-cream/75 hover:border-yellow hover:text-yellow"
            }`}
        >
            {label}
        </button>
    );
}

function ArticleCard({ article, index }) {
    const cover = resolveCover(article.cover_url);
    return (
        <motion.article
            initial={{ y: 24, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.5, delay: index * 0.05 }}
            data-testid={`blog-card-${article.slug}`}
            className="group relative border border-scd bg-elev rounded-sm overflow-hidden hover:border-yellow/60 transition-colors"
        >
            <Link to={`/blog/${article.slug}`} className="block">
                <div className="relative aspect-[16/10] bg-scd overflow-hidden">
                    {cover ? (
                        <img
                            src={cover}
                            alt={article.title}
                            loading="lazy"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                    ) : (
                        <div className="w-full h-full grid place-items-center bg-gradient-to-br from-[#f1ece0] to-[#faf7f1]">
                            <span className="font-display text-[#272727]/25 text-6xl">
                                112
                            </span>
                        </div>
                    )}
                    <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-yellow text-[#272727] text-[10px] font-semibold uppercase tracking-wide">
                        {article.category}
                    </span>
                </div>
                <div className="p-6">
                    <h3 className="font-display text-xl md:text-2xl text-cream leading-tight group-hover:text-yellow transition-colors">
                        {article.title}
                    </h3>
                    {article.excerpt && (
                        <p className="mt-3 text-cream/70 text-sm leading-relaxed line-clamp-3">
                            {article.excerpt}
                        </p>
                    )}
                    <div className="mt-5 flex items-center justify-between">
                        <span className="text-[11px] text-cream/50 flex items-center gap-1.5">
                            <Clock size={12} weight="duotone" />
                            {article.read_minutes} min de lecture
                        </span>
                        <ArrowUpRight
                            size={18}
                            weight="bold"
                            className="text-cream/60 group-hover:text-yellow transition-colors"
                        />
                    </div>
                </div>
            </Link>
        </motion.article>
    );
}
